import React, { useCallback } from 'react';
import { Star } from 'lucide-react';
import Link from 'next/link';

interface DetailedReviewData {
  averageReviewScore: number;
  reviewsCount: number;
  starDistribution: {
    '5 star': number;
    '4 star': number;
    '3 star': number;
    '2 star': number;
    '1 star': number;
  };
}
interface RatingDetailsPopoverProps {
  data: DetailedReviewData;
  reviewsLink?: string;
}

const STARS = ['5 star', '4 star', '3 star', '2 star', '1 star'] as const;

const renderStars = (rating: number) => {
  const stars = [];
  const maxStars = 5;
  const ratingFloor = Math.floor(rating);
  const decimalPart = rating - ratingFloor;

  for (let i = 1; i <= maxStars; i++) {
    if (i <= ratingFloor) {
      stars.push(
        <Star key={i} className="w-3 h-3 fill-[#948780] text-[#948780]" />
      );
    } else if (i === ratingFloor + 1 && decimalPart > 0) {
      stars.push(
        <div key={i} className="relative w-3 h-3">
          <Star className="w-3 h-3 text-gray-300 absolute top-0 left-0" />
          <div
            className="absolute top-0 left-0 overflow-hidden"
            style={{ width: `${decimalPart * 100}%` }}
          >
            <Star className="w-3 h-3 fill-[#948780] text-[#948780]" />
          </div>
        </div>
      );
    } else {
      stars.push(<Star key={i} className="w-3 h-3 text-gray-300" />);
    }
  }

  return stars;
};

export const RatingDetailsPopover: React.FC<RatingDetailsPopoverProps> = ({
  data,
  reviewsLink,
}) => {
  const renderRow = useCallback(
    (star: (typeof STARS)[number]) => {
      const percentage = data.starDistribution[star];
      const starCount = Math.round((percentage / 100) * data.reviewsCount);

      return (
        <div key={star} className="flex items-center mb-1">
          <div className="w-12 text-sm font-medium text-gray-700">{star}</div>
          <div className="flex-1 mx-2 h-2 bg-gray-200 rounded-full">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${percentage}%`,
                backgroundColor: '#948780',
              }}
            ></div>
          </div>

          <div className="w-10 flex items-center text-sm font-medium text-gray-700">
            {percentage === 0 ? 0 : `${percentage}%`}
            {/* {starCount !== 0 && <span className="ml-1">({starCount})</span>} */}
          </div>
        </div>
      );
    },
    [data]
  );

  return (
    <div className="p-4 w-64">
      <div className="flex font-bold items-center gap-2 mb-2">
        {renderStars(
          typeof data.averageReviewScore === 'number'
            ? data.averageReviewScore
            : parseFloat(data.averageReviewScore as string)
        )}
        <span>{data.averageReviewScore}</span>
        <span>out of 5</span>
      </div>

      <p className="text-sm font-semibold text-gray-900 mb-4">
        {data.reviewsCount} Total Reviews
      </p>

      {STARS.map(renderRow)}
      
      <Link href={reviewsLink ?? '#'} passHref className="text-sm text-[#7d726a] mx-17 font-semibold hover:text-[#786e67] cursor-pointer whitespace-nowrap">
        See all reviews
      </Link>
    </div>
  );
};
